/*
  custom.js — TIMEANAGRAM カスタムスクリプト
  ----------------------------------------------------------------
  テーマ本体（theme.js / vendor.min.js）は編集禁止。カスタムJSはすべてこのファイルに書く。

  【読み込み方式】type="module"
  本テーマは Prestige 11.4.0 = v7以降（ES modules + Web Components 世代）。
  layout/theme.liquid が importmap で "theme" / "vendor" / "photoswipe" を公開しており、
  custom.js も type="module" で読み込んでいるため import が使える。

    import { Drawer, fetchCart, throttle, debounce } from 'theme';
    import { animate } from 'vendor';

  ※ 会社ナレッジの Prestige 早見表にある「custom.js は classic script（defer）／import 不可」は
    v6以前の前提。本案件には適用しない（.cursor/rules/project.mdc「案件固有ルール 3」参照）。

  【実装ルール】詳細は .cursor/rules/custom-coding-rules.mdc
  - R1 各ブロックの直上に分類コメント（// custom / // app: {アプリ名}）を付ける
  - R2 未完成箇所には WIP マーカー（WIP + コロン）を入れる（main マージ時は残存ゼロ）
  - R6 要素の存在チェックを必ず行い（?. / early return）、グローバル汚染を避ける

  【テーマAPI】詳細は .cursor/rules/prestige-base.mdc「テーマAPI > v7+」
  - 既存コンポーネントは直接改変せず、継承して customElements.define() で拡張する
  - カート取得は生の /cart.js を連打せず、キャッシュ付きの fetchCart() を使う
  - カートイベントは document 上で発火:
      variant:change / variant:add / line-item:change / cart:change / cart:error / cart:refresh
  - UI再描画は cart:refresh を document.documentElement に dispatch する:
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
*/

// ============================================================================
// custom / 共通
// ============================================================================

// custom
// 来店予約「見たい商品リスト」（擬似カート）の localStorage ストア。
// c-reservation-button（商品ページ）と c-reservation-list（予約ページ）の両方から参照する。
// 一覧は購入用カートと分離して保持し、購入（実物確認）の予約画面に表示する。
// 購入（実物確認）の予約確定時に、商品名・SKU・本番商品URLを予約商品の
// line item propertiesへ添付し、Shopifyの注文詳細から確認できるようにする。
// custom: 1回の来店予約で確認する商品は最大10点。既存の超過リストは削除せず、お客様に整理してもらう。
const RESERVATION_LIST_MAX_ITEMS = 10;
const RESERVATION_LIST_LIMIT_MESSAGE = 'You can select up to 10 watches per reservation. Please remove a watch before adding another.';
const RESERVATION_LIST_OVER_LIMIT_MESSAGE = 'You can select up to 10 watches per reservation. Please reduce your list to 10 watches or fewer before selecting a date and time.';
const RESERVATION_LIST_STORAGE_KEY = 'timeanagram:reservation-list';
const RESERVATION_LIST_CHANGE_EVENT = 'reservation-list:change';
const RESERVATION_LIST_STORAGE_ERROR_MESSAGE =
  'We could not save your list. Please check your browser settings, including private browsing, and try again.';

// app: StockIQ
// main-collectionから渡された旧native sort_byクエリを初期表示時だけ除去する。
// sort_byが無いURLではreplaceしないため、通常のpage指定と無限リダイレクトを防ぐ。
if (!customElements.get('c-collection-sort-guard')) {
  class CollectionSortGuard extends HTMLElement {
    connectedCallback() {
      const currentUrl = new URL(window.location.href);
      const hasLegacySortQuery = currentUrl.searchParams.has('sort_by');

      if (!hasLegacySortQuery) return;

      currentUrl.searchParams.delete('sort_by');
      currentUrl.searchParams.delete('page');
      currentUrl.searchParams.delete('section_id');
      window.location.replace(currentUrl.toString());
    }
  }

  customElements.define('c-collection-sort-guard', CollectionSortGuard);
}

// custom
// localStorage から読み出した1件が「リスト項目として使える形」かを検証する。
// 破損データ（null・配列・id無し・title無し/空文字 等）は一覧描画や line item properties 生成で
// 例外や空文字プロパティを引き起こすため、読み出し時点で不正な要素だけを取り除く。
function isValidReservationListItem(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
  if (item.id == null) return false;
  if (typeof item.title !== 'string' || item.title.trim() === '') return false;
  return true;
}

function readReservationList() {
  try {
    const raw = window.localStorage.getItem(RESERVATION_LIST_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return [];

    return parsed.items.filter(isValidReservationListItem);
  } catch (error) {
    // 破損データ（不正なJSON等）は空配列にフォールバックする
    return [];
  }
}

// 保存に成功したら true、失敗（容量超過・プライベートブラウズ等）したら false を返す。
// 失敗時は変更イベントを発火しない（実際には保存されていない状態をUIに反映させないため）。
function writeReservationList(items) {
  try {
    window.localStorage.setItem(RESERVATION_LIST_STORAGE_KEY, JSON.stringify({ v: 1, items }));
  } catch (error) {
    return false;
  }

  document.dispatchEvent(new CustomEvent(RESERVATION_LIST_CHANGE_EVENT, { detail: { items } }));
  return true;
}

function isInReservationList(id) {
  return readReservationList().some((item) => String(item.id) === String(id));
}

// custom: 上限超過と保存失敗を区別し、呼び出し側で理由に応じた案内を表示する。
function addToReservationList(product) {
  const items = readReservationList();

  if (items.some((item) => String(item.id) === String(product.id))) {
    return { ok: true, items, reason: 'already-added' };
  }

  if (items.length >= RESERVATION_LIST_MAX_ITEMS) {
    return { ok: false, items, reason: 'limit-reached' };
  }

  const nextItems = items.concat([product]);
  const saved = writeReservationList(nextItems);

  if (!saved) {
    return { ok: false, items, reason: 'storage-failed' };
  }

  return { ok: true, items: nextItems };
}

// 戻り値の ok が false のときは保存に失敗している（呼び出し側でエラーメッセージを出すこと）。
function removeFromReservationList(id) {
  const nextItems = readReservationList().filter((item) => String(item.id) !== String(id));
  const saved = writeReservationList(nextItems);

  return { ok: saved, items: nextItems };
}

// 戻り値の ok が false のときは保存に失敗している（呼び出し側でエラーメッセージを出すこと）。
function clearReservationList() {
  const saved = writeReservationList([]);

  return { ok: saved, items: [] };
}

// app: StockIQ
// StockIQの並び順別コレクションを切り替えるリンクから、現在のタグパスとフィルター条件を引き継ぐ。
if (!customElements.get('c-collection-sort')) {
  class CollectionSort extends HTMLElement {
    connectedCallback() {
      const currentUrl = new URL(window.location.href);
      const droppedParams = new Set(['sort_by', 'page', 'section_id']);

      this.querySelectorAll('[data-collection-sort-link]').forEach((link) => {
        const targetPath = link.dataset.targetUrl;
        if (!targetPath) return;

        const targetUrl = new URL(targetPath, currentUrl.origin);

        // Liquidでlocale・タグパスまで完成したhrefへ、対象外の3項目を除いたクエリを追加する。
        targetUrl.search = '';
        currentUrl.searchParams.forEach((value, key) => {
          if (!droppedParams.has(key)) targetUrl.searchParams.append(key, value);
        });

        link.href = targetUrl.toString();
      });
    }
  }

  customElements.define('c-collection-sort', CollectionSort);
}

// ============================================================================
// custom / セクション別
// ============================================================================

// custom
// 商品ページ用「見たい商品リストに追加」ボタン（来店予約の擬似カート）。
// data-product 属性に商品情報のJSON（id/handle/title/url/image/price/sku）を持つ
// （snippets/custom-reservation-button.liquid が埋め込む）。
if (!customElements.get('c-reservation-button')) {
  class ReservationButton extends HTMLElement {
    connectedCallback() {
      this.buttonEl = this.querySelector('[data-reservation-button]');
      this.messageEl = this.querySelector('[data-reservation-message]');
      this.product = this.parseProduct();

      if (!this.buttonEl || !this.product) return;

      this.addLabel = this.getAttribute('data-button-text') || this.buttonEl.textContent.trim();
      this.removeLabel = this.getAttribute('data-remove-text') || this.addLabel;

      this.handleClick = this.handleClick.bind(this);
      this.handleListChange = this.handleListChange.bind(this);

      this.buttonEl.addEventListener('click', this.handleClick);
      document.addEventListener(RESERVATION_LIST_CHANGE_EVENT, this.handleListChange);

      this.render();
    }

    disconnectedCallback() {
      this.buttonEl?.removeEventListener('click', this.handleClick);
      document.removeEventListener(RESERVATION_LIST_CHANGE_EVENT, this.handleListChange);
    }

    parseProduct() {
      const raw = this.getAttribute('data-product');
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.id == null) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    handleClick() {
      if (!this.product) return;

      if (isInReservationList(this.product.id)) {
        const result = removeFromReservationList(this.product.id);

        if (!result.ok) {
          this.setMessage(RESERVATION_LIST_STORAGE_ERROR_MESSAGE);
          return;
        }

        this.setMessage('');
      } else {
        const result = addToReservationList(this.product);

        if (!result.ok) {
          this.setMessage(result.reason === 'limit-reached'
            ? RESERVATION_LIST_LIMIT_MESSAGE
            : RESERVATION_LIST_STORAGE_ERROR_MESSAGE);
          return;
        }

        this.setMessage('');
      }

      this.render();
    }

    handleListChange() {
      this.render();
    }

    setMessage(text) {
      if (this.messageEl) this.messageEl.textContent = text;
    }

    render() {
      if (!this.buttonEl || !this.product) return;

      const added = isInReservationList(this.product.id);
      this.buttonEl.textContent = added ? this.removeLabel : this.addLabel;
      this.buttonEl.setAttribute('aria-pressed', String(added));
    }
  }

  customElements.define('c-reservation-button', ReservationButton);
}

// custom
// 予約ページ用「見たい商品リスト」表示コンポーネント（擬似カート本体）。
// リストの描画・個別削除・すべて削除を担う。来店目的「購入（実物確認）」
// パネルと「買取・下取り」パネル（下取りチェック時）の2箇所から呼び出される
// （snippets/custom-reservation-wishlist.liquid 経由。sections/custom-reservation-list.liquid が呼び出す）。
if (!customElements.get('c-reservation-list')) {
  class ReservationList extends HTMLElement {
    connectedCallback() {
      this.itemsEl = this.querySelector('[data-reservation-items]');
      this.emptyEl = this.querySelector('[data-reservation-empty]');
      this.errorEl = this.querySelector('[data-reservation-error]');
      this.countEl = this.querySelector('[data-reservation-count]');
      this.clearButton = this.querySelector('[data-reservation-clear]');

      if (this.emptyEl) {
        this.emptyEl.textContent = this.getAttribute('data-empty-text') || '';
      }

      if (!this.itemsEl) return;

      this.handleListChange = this.handleListChange.bind(this);
      document.addEventListener(RESERVATION_LIST_CHANGE_EVENT, this.handleListChange);

      this.clearButton?.addEventListener('click', () => {
        const result = clearReservationList();
        this.setError(result.ok ? '' : RESERVATION_LIST_STORAGE_ERROR_MESSAGE);
      });

      this.render(readReservationList());
    }

    disconnectedCallback() {
      document.removeEventListener(RESERVATION_LIST_CHANGE_EVENT, this.handleListChange);
    }

    handleListChange(event) {
      this.setError('');
      this.render(event.detail?.items ?? readReservationList());
    }

    setError(text) {
      if (this.errorEl) this.errorEl.textContent = text || '';
    }

    render(items) {
      if (!this.itemsEl) return;

      this.itemsEl.innerHTML = '';
      items.forEach((item) => {
        this.itemsEl.appendChild(this.buildRow(item));
      });

      const hasItems = items.length > 0;
      this.emptyEl?.toggleAttribute('hidden', hasItems);
      if (this.countEl) this.countEl.textContent = String(items.length);
      if (this.clearButton) this.clearButton.disabled = !hasItems;
      // custom: 制限導入前に保存された商品も残し、超過時は削除を案内する。
      this.setError(items.length > RESERVATION_LIST_MAX_ITEMS ? RESERVATION_LIST_OVER_LIMIT_MESSAGE : '');
    }

    buildRow(item) {
      const row = document.createElement('li');
      row.className = 'c-reservation_item';
      row.dataset.reservationId = item.id;

      const thumb = document.createElement('span');
      thumb.className = 'c-reservation_thumb';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        thumb.appendChild(img);
      }

      const info = document.createElement('span');
      info.className = 'c-reservation_info';

      const title = document.createElement('span');
      title.className = 'c-reservation_title';
      title.textContent = item.title || '';
      info.appendChild(title);

      const metaParts = [];
      if (item.sku) metaParts.push(`SKU: ${item.sku}`);
      if (item.price) metaParts.push(item.price);

      if (metaParts.length > 0) {
        const meta = document.createElement('span');
        meta.className = 'c-reservation_meta';
        meta.textContent = metaParts.join(' / ');
        info.appendChild(meta);
      }

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'link text-xs c-reservation_remove';
      removeButton.textContent = 'Remove';
      removeButton.addEventListener('click', () => {
        const result = removeFromReservationList(item.id);
        this.setError(result.ok ? '' : RESERVATION_LIST_STORAGE_ERROR_MESSAGE);
      });

      row.append(thumb, info, removeButton);

      return row;
    }




  }

  customElements.define('c-reservation-list', ReservationList);
}

// app: Sesami Booking App
// SPの日付とタイムゾーンの重なりを、タイムラインの開いたShadow DOM内だけで補正する。
// アプリ更新で内部要素名が変わった場合は再確認が必要。予約処理・日時データには触れない。
(() => {
  const observers = new Map();
  let frame = 0;
  let active = false;

  const stop = () => {
    active = false;
    cancelAnimationFrame(frame);
    frame = 0;
    observers.forEach((observer) => observer.disconnect());
    observers.clear();
  };

  const schedule = () => {
    if (active && !frame) frame = requestAnimationFrame(sync);
  };

  const observe = (root) => {
    if (!root || observers.has(root)) return;
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    observers.set(root, observer);
  };

  const addStyle = (root, css) => {
    if (!root || root.querySelector('[data-c-sesami-mobile-date]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-c-sesami-mobile-date', '');
    style.textContent = css;
    root.append(style);
  };

  function sync() {
    frame = 0;
    if (!active) return;
    for (const [root, observer] of observers) {
      if (root.host && !root.host.isConnected) {
        observer.disconnect();
        observers.delete(root);
      }
    }
    document.querySelectorAll('sesami-window').forEach((modal) => {
      const windowRoot = modal.shadowRoot;
      observe(windowRoot);
      windowRoot?.querySelectorAll('sesami-timeline-calendar').forEach((calendar) => {
        const calendarRoot = calendar.shadowRoot;
        observe(calendarRoot);
        const headerRoot = calendarRoot?.querySelector('sesami-header')?.shadowRoot;
        observe(headerRoot);
        addStyle(headerRoot, '@media (max-width: 699px) { .titleWrapper a.primaryTitle { font-size: 16px; } }');
        // 320px級では縮小だけで操作欄の幅を確保できないため、操作欄を次の行へ送る。
        addStyle(calendarRoot, '@media (max-width: 374px) { :host .calendarWrapper .navContainer { margin-top: 0; } }');
      });
    });
  }

  const start = () => {
    stop();
    active = true;
    observe(document.body);
    schedule();
    // モーダルを開いた直後の遅延定義にも対応する。閉じた後はschedule側で何もしない。
    ['sesami-window', 'sesami-timeline-calendar', 'sesami-header'].forEach((name) => {
      customElements.whenDefined(name).then(schedule);
    });
  };

  window.addEventListener('sesami:modal:opened', start);
  window.addEventListener('sesami:modal:closed', stop);
  window.addEventListener('pagehide', stop);
  if (document.querySelector('sesami-window')) start();
})();

// custom
// 来店予約の予約確定処理（案3・Sesami Experience公式フロー）。
//
// 【設計の要点】
// 1. 購入（実物確認）はauto-add-to-cartを使う。日時確定後の予約枠確保、
//    予約トークンと日時のhidden項目更新、/cart/add.jsはすべてSesami自身に任せる。
//    テーマ側はモーダルを開く前に見たい商品をline item propertiesへ書き、
//    sesami:cart:add後に予約トークン付きのチケットが実際にカートへ入ったことだけを確認する。
// 2. カートを一時的にでも「空」にする瞬間を作らない。
//    「予約チケットを先に入れてから、退避・記録済みの行だけを消す」順序で行う
//    （カートを一括で空にする処理は一切使わない）。
//
// 送信時の処理順序: 0 テーマ側ボタンで見たい商品・同意を検証→openExperience()
//                    1 Sesamiが予約枠を確保→hidden項目更新→FormData(form)で/cart/add.js
//                    2 sesami:cart:add後、予約トークンが一致するチケットを/cart.jsで確認
//                    3〜5 「GET /cart.js で取り直し→退避へ統合して上書き→記録したキーだけ
//                      /cart/update.js で quantity 0」を最大3回ループ（削除直前に退避を取り直す）
//                    6 /checkout へ遷移
//                    ロックは navigator.locks のコールバック終了で自動解放
// 復元時（ページ読み込み時）の処理順序: 0 navigator.locks でロック取得
//                                        1 退避の有無にかかわらずカートを読み、
//                                          残っている予約チケットを /cart/change.js で除去
//                                        2 退避が無ければここで終了
//                                        3 退避items を /cart/add.js（items配列）で追加
//                                        4 再度 GET /cart.js を読み、退避分が実際に入っているか
//                                          確認できてから退避を削除 + cart:refresh
//                                        5 確認できなければ退避は残す（次回再試行）
//                                        ロックは navigator.locks のコールバック終了で自動解放
// （sections/custom-reservation-list.liquid が呼び出す）

// custom
// line item properties に渡す値を安全な文字列へ正規化する。空文字・非文字列（オブジェクト等）は
// 空文字として扱い、呼び出し側で空チェックして disabled にする（注文画面を汚さない・例外を防ぐ）。
function toSafePropertyString(value) {
  if (typeof value === 'string') return value.trim();
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return ''; // オブジェクト・配列等は [object Object] のような無意味な文字列にしない
}

// custom
// プレビューURLを注文へ保存しないよう、見たい商品のURLをshop.url基準の本番商品URLへ正規化する。
// localStorageは利用者が書き換えられるため、/products/ 配下以外のURLは注文へ送らない。
function getReservationItemCanonicalUrl(item, storeUrl) {
  const handle = toSafePropertyString(item?.handle);
  const rawUrl = toSafePropertyString(item?.url) || (handle ? `/products/${encodeURIComponent(handle)}` : '');
  if (!rawUrl) return '';

  try {
    const baseUrl = new URL(storeUrl || window.location.origin, window.location.origin);
    const parsedUrl = new URL(rawUrl, baseUrl);
    if (!parsedUrl.pathname.startsWith('/products/')) return '';

    return new URL(`${parsedUrl.pathname}${parsedUrl.search}`, baseUrl.origin).href;
  } catch (error) {
    return '';
  }
}

// custom
// Shopify注文の注文明細で1行を見れば対象商品を特定できるよう、商品名・SKU・URLを1つの
// line item property値にまとめる。SKUやURLが無い商品は、存在する情報だけを送る。
function buildReservationItemPropertyValue(item, storeUrl) {
  const parts = [toSafePropertyString(item?.title)];
  const sku = toSafePropertyString(item?.sku);
  const url = getReservationItemCanonicalUrl(item, storeUrl);

  if (sku) parts.push(`SKU: ${sku}`);
  if (url) parts.push(`URL: ${url}`);

  return parts.filter(Boolean).join(' / ');
}

// custom: 0円予約チケットの表示用キーを英語にする。判定時は変更前の「目的」キーも受け付ける。
const RESERVATION_TICKET_PROPERTY_KEY = 'Service';

// custom
// 予約と購入カートの分離処理（案3 v3）用の退避データ。localStorage キー timeanagram:cart-backup に
// { v:1, savedAt, items:[{key, id, quantity, properties}], sesamiVariantId } の形で保存する。
// items は「予約チケット以外の line item」を GET /cart.js から記録したもの。
// sesamiVariantId は、退避データを別ページ（例: トップページ）に戻ってきたときに復元処理が
// 「カートに残っているのが予約チケットかどうか」を判定するために持たせている
// （復元はどのページの読み込みでも走るため、そのページのセクション設定に頼れない）。
// 有効期限は設けない（Codex指摘: 24時間で破棄すると、戻るのが遅れた人の商品を失う）。
const CART_BACKUP_STORAGE_KEY = 'timeanagram:cart-backup';

// custom
// 退避データの要素を検証する（key または id あり／quantity が正の数）。壊れた要素は捨てる
// （/cart/add.js への再投入で例外や意図しない0個追加を起こさないため）。
function isValidCartBackupItem(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;

  const hasIdentifier = (typeof item.key === 'string' && item.key !== '') || item.id != null;
  if (!hasIdentifier) return false;

  if (typeof item.quantity !== 'number' || !Number.isFinite(item.quantity) || item.quantity <= 0) return false;

  return true;
}

function readCartBackup() {
  try {
    const raw = window.localStorage.getItem(CART_BACKUP_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items) || typeof parsed.savedAt !== 'number') return null;

    const items = parsed.items.filter(isValidCartBackupItem);
    if (items.length === 0) return null;

    return {
      v: parsed.v || 1,
      savedAt: parsed.savedAt,
      items,
      sesamiVariantId: typeof parsed.sesamiVariantId === 'string' ? parsed.sesamiVariantId : '',
    };
  } catch (error) {
    // 破損データは「退避無し」扱いにする（元のカートを壊さないための安全側フォールバック）
    return null;
  }
}

// 保存に成功したら true、失敗（容量超過・プライベートブラウズ等）したら false を返す。
// 呼び出し側は false のとき、既に入れたチケットを取り除いてから中止する
// （退避に失敗した状態のまま先へ進むと、購入用カートの中身を特定できなくなるため）。
function writeCartBackup(items, sesamiVariantId) {
  try {
    window.localStorage.setItem(
      CART_BACKUP_STORAGE_KEY,
      JSON.stringify({ v: 1, savedAt: Date.now(), items, sesamiVariantId: sesamiVariantId || '' })
    );
    return true;
  } catch (error) {
    return false;
  }
}

function clearCartBackup() {
  try {
    window.localStorage.removeItem(CART_BACKUP_STORAGE_KEY);
  } catch (error) {
    // noop
  }
}

// custom
// 既存の退避（前回の送信で復元しきれていない分）を、新しく集めた行と統合する。
// 送信開始時に無条件で上書きすると前回分を捨ててしまうため、同じ key の行は新しい方
// （最新の quantity/properties）で置き換え、それ以外は両方とも残す。
function mergeCartBackupItems(existingItems, newItems) {
  const kept = existingItems.filter(
    (existingItem) =>
      !newItems.some((newItem) => existingItem.key && newItem.key && existingItem.key === newItem.key)
  );
  return kept.concat(newItems);
}

// custom
// カート内の line item が「来店予約チケット」かどうかの判定。Service（旧「目的」）の存在 かつ
// variant_id が sesami_variant_id と一致、の両方を見る（片方だけでは誤判定しうるため）。
function isReservationTicketCartItem(item, sesamiVariantId) {
  const hasPurposeProperty = Boolean(item && item.properties && (item.properties[RESERVATION_TICKET_PROPERTY_KEY] || item.properties['目的']));
  if (!hasPurposeProperty) return false;
  if (!sesamiVariantId) return false;
  return String(item.variant_id) === String(sesamiVariantId);
}

// custom
// 退避が無いページ読み込みでも、そのページに来店予約フォームが存在すれば0円予約商品の
// バリアントIDをDOMから拾う。復元処理が「退避が無くてもカートに残ったチケットを除去する」
// ために使う（退避にしか sesamiVariantId を持たせていないため）。
function getSesamiVariantIdFromDom() {
  const form = document.querySelector('c-reservation-form[data-sesami-variant-id]');
  return (form?.getAttribute('data-sesami-variant-id') || '').trim();
}

// custom
// 送信処理・復元処理が同時に走ってカートを壊さないための排他制御。navigator.locks
// （Web Locks API）を使う。localStorage の read-then-write 方式は比較更新（CAS）が無く、
// 「A/Bが空を読む→A書き込み確認→B書き込み確認」で両方成功しうる（同時取得を防げない）ため、
// 同一オリジンのタブ間でアトミックに排他できる Web Locks API に置き換えた。
async function withCartLock(fn, waitForLock = false) {
  if (navigator.locks?.request) {
    if (waitForLock) {
      return navigator.locks.request('timeanagram:cart', async () => ({ skipped: false, value: await fn() }));
    }

    return navigator.locks.request('timeanagram:cart', { ifAvailable: true }, async (lock) => {
      if (!lock) return { skipped: true };
      return { skipped: false, value: await fn() };
    });
  }
  // 非対応ブラウザは単純に実行する（排他なし）
  return { skipped: false, value: await fn() };
}

// custom
// Shopify Ajax Cart API の素の呼び出し。予約フローは「先に入れて、後から記録した分だけ消す」を
// ミスなく直列実行する必要があるため、キャッシュを挟まず直接 fetch する。
function getCartRoute(path) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return `${root}${path}`;
}

async function fetchCartJson() {
  const response = await fetch(getCartRoute('cart.js'), { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('cart.js の取得に失敗しました');
  return response.json();
}

// app: Sesami Booking App
// auto-add-to-cartのイベントは「追加処理の開始」で発火し、/cart/add.jsの完了は通知しない。
// Sesamiが生成した予約トークンとバリアントIDが一致する行を/cart.jsで待ち、別の予約を
// 誤ってチェックアウトしないようにする。
async function waitForSesamiReservationTicket(sesamiVariantId, reservationToken) {
  const maxAttempts = 40;
  const intervalMs = 250;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const cart = await fetchCartJson();
      const ticketLine = (cart.items || []).find((item) => {
        const token = item?.properties?._reservation_token;
        return (
          isReservationTicketCartItem(item, sesamiVariantId) &&
          typeof token === 'string' &&
          token === reservationToken
        );
      });

      if (ticketLine) return ticketLine;
    } catch (error) {
      // Sesamiの/cart/add.jsとカート取得が一時的に競合することがあるため、上限まで再試行する。
    }

    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }

  return null;
}

async function postCartChangeToZero(lineKey) {
  const response = await fetch(getCartRoute('cart/change.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 0 }),
  });
  if (!response.ok) throw new Error('cart/change.js に失敗しました');
  return response.json();
}

// 「記録したキーだけ」quantity 0 にする。チケット以外すべてではない
// （別タブで増えた商品まで消してしまわないため）。
async function postCartUpdateQuantitiesToZero(lineKeys) {
  if (!lineKeys || lineKeys.length === 0) return null;

  const updates = {};
  lineKeys.forEach((key) => {
    updates[key] = 0;
  });

  const response = await fetch(getCartRoute('cart/update.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!response.ok) throw new Error('cart/update.js に失敗しました');
  return response.json();
}

// 退避しておいた items をまとめて追加する（復元用）。/cart/add.js の items 配列は既に
// GET /cart.js から取得した実在の id/quantity/properties を使うだけなので、
// 「手組みpayloadの禁止」（Sesamiのチケット送信の話）には当たらない。
async function postCartAddItems(items) {
  if (!items || items.length === 0) return null;

  const response = await fetch(getCartRoute('cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error('cart/add.js（復元）に失敗しました');
  return response.json();
}

// カートから「チケット以外の line item」を key/id/quantity/properties 付きで取り出す。
function collectNonTicketCartLines(cart, ticketKey) {
  return (cart.items || [])
    .filter((item) => item.key !== ticketKey)
    .map((item) => ({
      key: item.key,
      id: item.id,
      quantity: item.quantity,
      properties: item.properties || {},
    }));
}

// custom
// 予約フォーム全体を制御する Web Component。
// data-booking-mode="hybrid" では購入（実物確認）だけを0円チェックアウトで確定し、
// 修理・買取は各Sesami ExperienceのInstant Bookingに任せる。
// （sections/custom-reservation-list.liquid が呼び出す）
if (!customElements.get('c-reservation-form')) {
  const PURPOSE_PURCHASE = '購入（実物確認）';
  const PURPOSE_REPAIR = '修理';
  const PURPOSE_BUYBACK = '買取・下取り';
  const SESAMI_LOADED_EVENT = 'sesami:loaded';
  const SESAMI_CART_ADD_EVENT = 'sesami:cart:add';
  const SESAMI_RESERVATION_FAILED_EVENT = 'sesami:reservation:failed';

  const PURPOSE_PANEL_MAP = {
    [PURPOSE_PURCHASE]: 'purchase',
    [PURPOSE_REPAIR]: 'repair',
    [PURPOSE_BUYBACK]: 'buyback',
  };

  class ReservationForm extends HTMLElement {
    connectedCallback() {
      this.formEl = this.querySelector('[data-reservation-form]');
      this.submitButton = this.querySelector('[data-reservation-submit]');
      this.messageEl = this.querySelector('[data-reservation-submit-message]');
      this.purchaseExperience = this.formEl?.querySelector('sesami-experience');
      this.bookingMode = this.getAttribute('data-booking-mode') || 'checkout';
      this.isInstantBooking = this.bookingMode === 'instant';
      this.disableInactivePanelFields = this.bookingMode === 'checkout';
      this.purposeInputs = Array.from(this.querySelectorAll('[data-purpose-input]'));
      this.tradeinCheckbox = this.querySelector('[data-tradein-input]');
      this.tradeinWishlist = this.querySelector('[data-tradein-wishlist]');
      this.repairPurchasedInput = this.querySelector('[data-repair-purchased-input]');

      if (!this.formEl || this.purposeInputs.length === 0) return;

      this.handlePurposeChange = this.handlePurposeChange.bind(this);
      this.purposeInputs.forEach((input) => input.addEventListener('change', this.handlePurposeChange));
      this.updatePanels();

      // app: Sesami Booking App
      // Instant Bookingは各<sesami-experience>がフォーム送信と注文作成を完結させる。
      // テーマ側の旧チェックアウト処理は接続せず、目的別パネルの切替だけを残す。
      if (this.isInstantBooking) return;
      if (!this.submitButton) return;

      this.repairSkuInput = this.formEl.querySelector('[name="repair_sku"]');
      this.buybackItemInput = this.formEl.querySelector('[name="buyback_item"]');

      this.sesamiVariantId = (this.getAttribute('data-sesami-variant-id') || '').trim();
      this.storeUrl = (this.getAttribute('data-store-url') || '').trim();
      this.noVariantMessage = this.getAttribute('data-no-variant-message') || '';
      this.wishlistRequiredMessage = this.getAttribute('data-wishlist-required-message') || '';
      this.errorMessage = this.getAttribute('data-error-message') || '';
      this.storageErrorMessage = this.getAttribute('data-storage-error-message') || '';
      this.isCheckoutProcessing = false;

      this.handleTradeinChange = this.handleTradeinChange.bind(this);
      this.handleOpenExperience = this.handleOpenExperience.bind(this);
      this.handleSesamiLoaded = this.handleSesamiLoaded.bind(this);
      this.handleSesamiCartAdd = this.handleSesamiCartAdd.bind(this);
      this.handleSesamiReservationFailed = this.handleSesamiReservationFailed.bind(this);
      this.updateSesamiReadyState = this.updateSesamiReadyState.bind(this);

      this.tradeinCheckbox?.addEventListener('change', this.handleTradeinChange);
      // app: Sesami Booking App
      // 公式Custom buttonで日時選択を開き、Sesami自身のauto-add-to-cart完了後だけ
      // テーマ側の0円チェックアウト処理を続行する。
      this.submitButton.addEventListener('click', this.handleOpenExperience);
      window.addEventListener(SESAMI_LOADED_EVENT, this.handleSesamiLoaded);
      window.addEventListener(SESAMI_CART_ADD_EVENT, this.handleSesamiCartAdd);
      window.addEventListener(SESAMI_RESERVATION_FAILED_EVENT, this.handleSesamiReservationFailed);

      // sesami:loadedより後にこのWeb Componentが初期化されるケースに備え、
      // tag-idに対応するhidden fields wrapperの挿入も監視する。
      this.sesamiReadyObserver = new MutationObserver(this.updateSesamiReadyState);
      this.sesamiReadyObserver.observe(this.formEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tag-id'],
      });
      this.updateSesamiReadyState();

      this.updateTradeinWishlist();

      if (!this.sesamiVariantId) {
        // 0円予約商品が未設定。カートには一切手を付けさせず案内だけ出す
        this.submitButton.disabled = true;
        this.setMessage(this.noVariantMessage);
      }
    }

    disconnectedCallback() {
      this.purposeInputs.forEach((input) => input.removeEventListener('change', this.handlePurposeChange));
      this.tradeinCheckbox?.removeEventListener('change', this.handleTradeinChange);
      this.submitButton?.removeEventListener('click', this.handleOpenExperience);
      window.removeEventListener(SESAMI_LOADED_EVENT, this.handleSesamiLoaded);
      window.removeEventListener(SESAMI_CART_ADD_EVENT, this.handleSesamiCartAdd);
      window.removeEventListener(SESAMI_RESERVATION_FAILED_EVENT, this.handleSesamiReservationFailed);
      this.sesamiReadyObserver?.disconnect();
    }

    get purpose() {
      return this.purposeInputs.find((input) => input.checked)?.value || '';
    }

    handlePurposeChange() {
      this.updatePanels();
    }

    handleTradeinChange() {
      this.updateTradeinWishlist();
    }

    // 目的に応じて該当パネルだけ表示する。hybrid/Instant BookingではSesamiが差し込む要素を
    // disabledにするとウィジェットを壊すためhiddenだけを切り替える。全目的を1つのformで送る
    // 旧checkoutモードだけ、非表示パネル内の入力欄をdisabledにする。
    updatePanels() {
      const activeKey = PURPOSE_PANEL_MAP[this.purpose] || '';

      this.querySelectorAll('[data-purpose-panel]').forEach((panel) => {
        const isActive = panel.dataset.purposePanel === activeKey;
        panel.toggleAttribute('hidden', !isActive);

        if (this.disableInactivePanelFields) {
          panel.querySelectorAll('input, textarea, select').forEach((field) => {
            field.disabled = !isActive;
          });
        }
      });
    }

    updateTradeinWishlist() {
      if (!this.tradeinWishlist) return;

      const show = Boolean(this.tradeinCheckbox?.checked);
      this.tradeinWishlist.toggleAttribute('hidden', !show);

      this.tradeinWishlist.querySelectorAll('input, textarea, select').forEach((field) => {
        field.disabled = !show;
      });
    }

    setMessage(text) {
      if (this.messageEl) this.messageEl.textContent = text || '';
    }

    // 送信直前に、見たい商品を「Watch 1」「Watch 2」…のline item propertiesへ変換する。
    // 再送時に重複しないよう、前回生成したhidden inputを先に取り除く。
    writePropertiesFromState(items) {
      this.formEl
        .querySelectorAll('[data-reservation-wishlist-property]')
        .forEach((input) => input.remove());

      items.forEach((item, index) => {
        const value = buildReservationItemPropertyValue(item, this.storeUrl);
        if (!value) return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[Watch ${index + 1}]`;
        input.value = value;
        input.setAttribute('data-reservation-wishlist-property', '');
        this.formEl.appendChild(input);
      });
    }

    // app: Sesami Booking App
    // Experienceとhidden fields wrapperに共通するtag-idを使い、購入サービス自身の予約項目を取得する。
    // wrapperが購入form内にあることも確認し、他サービスの予約値は使わない。
    getPurchaseSesamiHiddenFieldsWrapper() {
      const tagId = (this.purchaseExperience?.getAttribute('tag-id') || '').trim();
      if (!tagId) return null;

      const wrapper = document.getElementById(`sesami-hidden-fields-${tagId}`);
      const isPurchaseWrapper =
        wrapper?.classList.contains('sesami-hidden-fields-wrapper') && wrapper.closest('form') === this.formEl;
      return isPurchaseWrapper ? wrapper : null;
    }

    isPurchaseSesamiEvent(event) {
      const tagId = (this.purchaseExperience?.getAttribute('tag-id') || '').trim();
      return Boolean(tagId && event?.detail?.tagId === tagId);
    }

    updateSesamiReadyState() {
      const isReady = Boolean(
        this.sesamiVariantId &&
          this.purchaseExperience &&
          typeof this.purchaseExperience.openExperience === 'function' &&
          this.getPurchaseSesamiHiddenFieldsWrapper()
      );

      if (!isReady) return;

      this.isSesamiReady = true;
      this.sesamiReadyObserver?.disconnect();
      if (!this.isCheckoutProcessing) this.submitButton.disabled = false;
    }

    handleSesamiLoaded(event) {
      if (!this.isPurchaseSesamiEvent(event)) return;

      // Sesamiはloadedイベントの直後にhidden fields wrapperを挿入するため、
      // 同期処理が終わった次のマイクロタスクで準備完了を判定する。
      queueMicrotask(this.updateSesamiReadyState);
    }

    handleOpenExperience(event) {
      event.preventDefault();
      if (!this.formEl || !this.submitButton || this.submitButton.disabled || this.isCheckoutProcessing) return;

      if (!this.sesamiVariantId) {
        this.setMessage(this.noVariantMessage);
        return;
      }

      // 購入（実物確認）では見たい商品が予約の主データになるため、0件の予約は送信しない。
      const wishlistItems = readReservationList();
      if (this.purpose !== PURPOSE_PURCHASE || wishlistItems.length === 0) {
        this.setMessage(this.wishlistRequiredMessage);
        return;
      }

      // custom: 保存済みの超過リストでも日時選択・予約枠確保を開始させない。
      if (wishlistItems.length > RESERVATION_LIST_MAX_ITEMS) {
        this.setMessage(RESERVATION_LIST_OVER_LIMIT_MESSAGE);
        return;
      }

      this.setMessage('');

      if (!this.formEl.checkValidity()) {
        this.formEl.reportValidity();
        return;
      }

      if (!this.isSesamiReady || !this.getPurchaseSesamiHiddenFieldsWrapper()) {
        this.setMessage(this.errorMessage);
        return;
      }

      // openExperience()の前に書き込むことで、日時確定後にSesamiが作る
      // FormData(form)へ見たい商品の商品名・SKU・URLをそのまま含める。
      this.writePropertiesFromState(wishlistItems);
      this.setMessage('');
      this.purchaseExperience.openExperience();
    }

    handleSesamiReservationFailed(event) {
      if (!this.isPurchaseSesamiEvent(event)) return;

      this.resetCheckoutProcessing(this.errorMessage);
    }

    handleSesamiCartAdd(event) {
      if (!this.isPurchaseSesamiEvent(event) || this.isCheckoutProcessing) return;

      const reservationTokenInput = this.getPurchaseSesamiHiddenFieldsWrapper()?.querySelector(
        '[sesami-hidden-reservation-token]'
      );
      const reservationToken = (reservationTokenInput?.value || '').trim();
      if (!reservationToken) {
        this.resetCheckoutProcessing(this.errorMessage);
        return;
      }

      this.isCheckoutProcessing = true;
      this.submitButton.disabled = true;
      this.setMessage('');

      // SesamiのイベントディスパッチはPromiseを待たないため、非同期処理を明示的に開始する。
      this.processSesamiCartAdd(reservationToken).catch(() => {
        this.resetCheckoutProcessing(this.errorMessage);
      });
    }

    async processSesamiCartAdd(reservationToken) {
      // Sesamiの/cart/add.jsがまだ完了していない間もロック内で待つ。別タブの復元処理と
      // 競合したときは放置せず、順番が来るまで待ってチケットを確実に処理する。
      await withCartLock(async () => {
        const ticketLine = await waitForSesamiReservationTicket(this.sesamiVariantId, reservationToken);
        if (!ticketLine) {
          this.resetCheckoutProcessing(this.errorMessage);
          return;
        }

        await this.finalizeReservationCheckout(ticketLine.key);
      }, true);
    }

    resetCheckoutProcessing(message) {
      this.isCheckoutProcessing = false;
      this.setMessage(message);
      if (this.isSesamiReady && this.sesamiVariantId) this.submitButton.disabled = false;
    }

    async finalizeReservationCheckout(ticketKey) {
      // 送信開始時に既存の退避（前回失敗分など）があれば、捨てずに新しい退避へ統合する
      const previousBackup = readCartBackup();
      let carriedOverItems = previousBackup ? previousBackup.items : [];

      // ---- 2〜4. 「GET /cart.js で取り直し → 退避へ統合して上書き → 記録したキーだけ
      //            /cart/update.js で quantity 0」を最大3回ループする。
      // 毎回、削除の直前に取り直した内容で退避を上書きするので、退避と削除対象が必ず一致する
      // （記録→時間差→削除、の間に別タブが同じバリアントを増やして合流するケースに対応）。
      let cleared = false;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        let cart;
        try {
          cart = await fetchCartJson();
        } catch (error) {
          // カートの現状が読めない＝何を退避すべきか分からない。チケットだけ取り除いて中止する
          await this.safeRemoveTicket(ticketKey);
          this.resetCheckoutProcessing(this.errorMessage);
          return;
        }

        const otherLines = collectNonTicketCartLines(cart, ticketKey);

        if (otherLines.length === 0) {
          cleared = true;
          break;
        }

        const mergedItems = mergeCartBackupItems(carriedOverItems, otherLines);
        const backedUp = writeCartBackup(mergedItems, this.sesamiVariantId);
        if (!backedUp) {
          // 書けなかった: 手順1で入れたチケットを /cart/change.js で取り除いてからエラー表示して
          // 中止する（＝カートを完全に元通りにしてから諦める）
          await this.safeRemoveTicket(ticketKey);
          this.resetCheckoutProcessing(this.storageErrorMessage);
          return;
        }
        carriedOverItems = mergedItems;

        try {
          await postCartUpdateQuantitiesToZero(otherLines.map((line) => line.key));
        } catch (error) {
          // 失敗: 退避は消さない（次回ページロードの自動復元に委ねる）。チケット除去も試みない
          // （サーバー側で削除が成功している可能性があり、状態が不明なため）。メッセージだけ出す
          this.resetCheckoutProcessing(this.errorMessage);
          return;
        }
      }

      if (!cleared) {
        // 3回試しても残った。退避を残したまま中止する（チケットは復元処理が回収する）
        this.resetCheckoutProcessing(this.errorMessage);
        return;
      }

      // ---- 5. window.location.href = '/checkout' ----
      window.location.href = '/checkout';
    }

    // 途中失敗時の後始末。チケットを /cart/change.js で quantity 0 にする（一括クリア系のAPIは使わない）。
    async safeRemoveTicket(ticketKey) {
      if (!ticketKey) return;

      try {
        await postCartChangeToZero(ticketKey);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        // ここでの失敗はもう打つ手が無い（チケットが残る可能性がある）。退避データは残し、
        // 次回ページロード時の自動復元（restoreCartIfNeeded）に委ねる
      }
    }

  }

  customElements.define('c-reservation-form', ReservationForm);
}

// custom
// チェックアウトから戻ってきたとき（離脱・完了いずれも）の自動復元（案3 v3）。
// custom.js は全ページで読み込まれるため、予約ページ以外に戻ってきたケースもここで拾う。
async function restoreCartIfNeeded() {
  // ---- 0. navigator.locks でロック取得（取れなければ何もしない） ----
  await withCartLock(async () => {
    const backup = readCartBackup();

    // 退避が無くても、このページに来店予約フォームがあればバリアントIDをDOMから拾い、
    // カートに残った予約チケットを除去できるようにする（送信途中でチケット除去自体が
    // 失敗した場合、退避が無いため何もしないと残り続けてしまうため）
    const sesamiVariantId = backup?.sesamiVariantId || getSesamiVariantIdFromDom();
    if (!backup && !sesamiVariantId) return; // することが無い

    let cart;
    try {
      cart = await fetchCartJson();
    } catch (error) {
      return; // 読めなかった。退避は残し、次回ページロード時に再試行する
    }

    // ---- 1. 退避の有無にかかわらず、カートに予約チケットが残っていれば /cart/change.js で除去する ----
    if (sesamiVariantId) {
      const ticketLine = (cart.items || []).find((item) => isReservationTicketCartItem(item, sesamiVariantId));

      if (ticketLine) {
        try {
          await postCartChangeToZero(ticketLine.key);
        } catch (error) {
          return; // 取り除きに失敗。退避は残し、次回再試行する
        }
      }
    }

    // ---- 2. 退避が無ければここで終了（チケット除去のみ実施済み） ----
    if (!backup) return;

    // ---- 3. 退避した items を POST /cart/add.js（items配列でまとめて）で追加する ----
    try {
      await postCartAddItems(
        backup.items.map((item) => ({ id: item.id, quantity: item.quantity, properties: item.properties || {} }))
      );
    } catch (error) {
      // 応答取得にだけ失敗した可能性がある（サーバー側は追加に成功しているかもしれない）。
      // ここでは諦めず、下の確認ステップに進んで実際の反映状況を見る
    }

    // ---- 4. 冪等性の確認: 再度 GET /cart.js を読み、退避分が実際に入っているか確認できてから
    //         退避を削除する。応答取得に失敗しても、この確認により二重追加を避けられる ----
    let confirmCart;
    try {
      confirmCart = await fetchCartJson();
    } catch (error) {
      return; // 確認できない。退避は残す（次回再試行）
    }

    const isRestored = backup.items.every((backupItem) =>
      (confirmCart.items || []).some(
        (cartItem) => String(cartItem.id) === String(backupItem.id) && cartItem.quantity >= backupItem.quantity
      )
    );

    if (!isRestored) return; // まだ反映されていない（本当に失敗）。退避は残し、次回再試行する

    // ---- 5. 確認できたら退避を削除し、cart:refresh を dispatch ----
    clearCartBackup();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  });
}

// 通常のページ読み込みで1回。加えて bfcache からの復帰（チェックアウトからブラウザの「戻る」で
// 戻ってきた場合など、モジュールが再実行されずページだけが復元されるケース）でも再試行する。
restoreCartIfNeeded();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) restoreCartIfNeeded();
});

// custom
// コレクションの顧客向けタグ絞り込み。
// 既存のPrestige facets-formへ、選択したタグをコレクションURLのパスとして渡す。
// 同じ分類内は1件に置き換え、分類をまたぐタグは「+」で連結してAND条件にする。
if (!customElements.get('c-tag-facets')) {
  class TagFacets extends HTMLElement {
    connectedCallback() {
      this.formEl = this.closest('form');
      this.collectionUrl = (this.getAttribute('data-collection-url') || '').replace(/\/+$/, '');

      if (!this.formEl || !this.collectionUrl) return;

      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);
      this.syncFormAction();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    handleClick(event) {
      const option = event.target.closest?.('[data-tag-filter-option]');
      if (!option || !this.contains(option)) return;

      const wasActive = option.getAttribute('aria-pressed') === 'true';

      this.querySelectorAll('[data-tag-filter-option]').forEach((candidate) => {
        if (candidate.dataset.tagGroup === option.dataset.tagGroup) {
          this.setOptionState(candidate, false);
        }
      });

      if (!wasActive) this.setOptionState(option, true);

      this.syncFormAction();
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setOptionState(option, isActive) {
      option.setAttribute('aria-pressed', String(isActive));
      option.classList.toggle('is-active', isActive);
      option.classList.toggle('link-faded', !isActive);
    }

    syncFormAction() {
      if (!this.formEl || !this.collectionUrl) return;

      const activeTags = Array.from(this.querySelectorAll('[data-tag-filter-option][aria-pressed="true"]'))
        .map((option) => option.dataset.tagHandle)
        .filter(Boolean);
      const tagPath = activeTags.length > 0 ? `/${activeTags.join('+')}` : '';

      this.formEl.setAttribute('action', `${this.collectionUrl}${tagPath}`);
    }
  }

  customElements.define('c-tag-facets', TagFacets);
}

// ============================================================================
// app: / アプリ連携
// ============================================================================
